import { PLAN_NAMES, SUPPLIER_PLANS } from "constants/data";
import dayjs from "dayjs";
import { isValidPlan } from "helpers/plan";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getUser } from "redux/selectors/user";
import { UserPlanType } from "types/user";

const useUser = () => {
  const user = useSelector(getUser) || {};
  const { plan = {}, balance, supplier, buyer } = user;

  const now = dayjs();

  const currentPlan: UserPlanType = useMemo(() => {
    // Try to get plan from supplier first, then buyer, then fallback to user plan
    const activePlan = supplier?.plan || buyer?.plan || plan;

    return {
      ...(SUPPLIER_PLANS.find((p) => p.label === activePlan?.plan_name) ||
        SUPPLIER_PLANS.find((p) => p.label === PLAN_NAMES.free)),
      ...activePlan,
      isExpired: activePlan?.end_date
        ? dayjs(activePlan.end_date).isBefore(now)
        : false,
    };
  }, [plan, supplier, buyer, now]);

  const isFreePlan = useMemo(() => !isValidPlan(currentPlan), [currentPlan]);

  // Get balance from supplier if available, otherwise use user balance
  const userBalance = supplier?.balance || balance || {};

  const buyerDetail = useMemo(
    () => ({
      plan: buyer?.plan || {},
      isBuyer: user.is_buyer === true,
    }),
    [buyer, user.is_buyer]
  );

  const supplierDetail = useMemo(
    () => ({
      plan: supplier?.plan || {},
      balance: supplier?.balance || {},
      supplierId: supplier?.supplier_id,
      isSupplier: user.is_supplier === 1,
      isBizVerified: supplier?.plan?.is_biz_verified,
      isVerifiedWorkEmail: supplier?.plan?.is_verified_work_email,
    }),
    [supplier, user.is_supplier]
  );

  return useMemo(
    () => ({
      // User basic info
      user,
      isLoggedIn: user.is_logged_in,
      currentPlan,
      isFreePlan,
      isSupplier: user.is_supplier === 1,
      userContactQuote: balance ? Number(balance?.contact_quota || 0) : 0,
      // User profile details
      profile: {
        customerName: user.customer_name,
        email: user.email,
        fdCountry: user.fd_country,
        language: user.language,
        companyName: user.company_name,
        currency: user.currency,
        position: user.position,
        customerPhone: user.customer_phone,
        userImage: user.user_image,
        uuid: user.uuid,
        name: user.name,
        companyLogo: user.company_logo,
        isPrimary: user.is_primary === 1,
        isVerifiedWorkEmail: user.is_verified_work_email === 1,
        identity: user.identity,
        avatar: user.avatar,
        firstName: user.first_name,
      },

      // Balance information
      balance: {
        userPoint: userBalance ? Number(userBalance?.balance || 0) : 0,
        userContactQuote: userBalance
          ? Number(userBalance?.contact_quota || 0)
          : 0,
        conversationQuota: userBalance
          ? Number(userBalance?.conversation_quota || 0)
          : 0,
      },

      // Buyer and Supplier specific details
      buyerDetail,
      supplierDetail,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, currentPlan, isFreePlan, userBalance, buyerDetail, supplierDetail]
  );
};

export default useUser;
